import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import { Employee, EmployeeFormData, EmployeeFilters, EmployeeSortOptions, EmployeeStats } from '../types/employee.ts';
import { Contractor } from '../types/contractor.ts';
import { EmployeeImporter } from '../utils/employeeImport.ts';
import { calculateBurdenedRate, calculateCustomAdjustments, validateSkillsRatings, getSkillRatingText, calculateContractorRates } from '../utils/rateCalculations.ts';
import TDLRLicenseManagement from './TDLRLicenseManagement.tsx';
import EmployeeDataImporter from './EmployeeDataImporter.tsx';

const EmployeeManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDataImportModal, setShowDataImportModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [employeeTimesheets, setEmployeeTimesheets] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sortField, setSortField] = useState<'weekEnding' | 'contractor' | 'jobName' | 'hours'>('weekEnding');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'employees' | 'tdlr'>('employees');
  const [stats, setStats] = useState<EmployeeStats>({
    total: 0,
    active: 0,
    inactive: 0,
    dismissed: 0,
    expiredDocuments: 0,
    upcomingExpirations: 0
  });

  // Filters and sorting
  const [filters, setFilters] = useState<EmployeeFilters>({
    search: '',
    status: 'all',
    jobTitle: '',
    hasExpiredDocuments: false,
    hasUpcomingExpirations: false
  });
  const [sortOptions, setSortOptions] = useState<EmployeeSortOptions>({
    field: 'name',
    direction: 'asc'
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  // Form data
  const [formData, setFormData] = useState<EmployeeFormData>({
    name: '',
    phone: '',
    employeeNumber: '',
    hireDate: '',
    jobTitle: '',
    hourlyWage: '',
    fica: '',
    insurance: '',
    vacation: '',
    carFuel: '',
    markup: '',
    vacationHoursUsed: '',
    vacationYear: new Date().getFullYear().toString(),
    // Skills (1-5 rating)
    handToolKnowledge: '',
    installLightsOutlets: '',
    installWire: '',
    installBendConduit: '',
    powerToolKnowledge: '',
    pullWire: '',
    readBlueprints: '',
    repairWiringSystem: '',
    idType: 'DL',
    idExpires: '',
    application: false,
    tdlrNumber: '',
    tdlrExpires: '',
    cecEmployeeNumber: '',
    scissorLiftTraining: '',
    firstAidCpr: '',
    status: 'active',
    notes: ''
  });

  // State for expandable card view
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({});


  useEffect(() => {
    fetchEmployees();
    fetchContractors();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [employees, filters, sortOptions]);

  useEffect(() => {
    calculateStats();
  }, [employees]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, orderBy('name'));
      const snapshot = await getDocs(q);
      
      const employeeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        hireDate: doc.data().hireDate?.toDate() || new Date(),
        idExpires: doc.data().idExpires?.toDate(),
        tdlrExpires: doc.data().tdlrExpires?.toDate(),
        scissorLiftTraining: doc.data().scissorLiftTraining?.toDate(),
        firstAidCpr: doc.data().firstAidCpr?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Employee[];
      
      setEmployees(employeeData);
    } catch (err) {
      setError('Failed to fetch employees');
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateEmployeeLocal = (employeeId: string, updates: any) => {
    setEmployees(prevEmployees => 
      prevEmployees.map(emp => 
        emp.id === employeeId 
          ? { ...emp, ...updates }
          : emp
      )
    );
  };

  const fetchContractors = async () => {
    try {
      const contractorsRef = collection(db, 'contractors');
      const q = query(contractorsRef, orderBy('companyName'));
      const snapshot = await getDocs(q);
      
      const contractorData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        licenseExpires: doc.data().licenseExpires?.toDate(),
        insuranceExpires: doc.data().insuranceExpires?.toDate(),
        bondExpires: doc.data().bondExpires?.toDate(),
        lastLoginAt: doc.data().lastLoginAt?.toDate()
      })) as Contractor[];
      
      setContractors(contractorData);
    } catch (err) {
      console.error('Error fetching contractors:', err);
      // Don't set error for contractors as it's not critical for employee management
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...employees];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.name.toLowerCase().includes(searchLower) ||
        emp.jobTitle?.toLowerCase().includes(searchLower) ||
        emp.tdlrNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(emp => emp.status === filters.status);
    }

    // Apply job title filter
    if (filters.jobTitle) {
      filtered = filtered.filter(emp => emp.jobTitle === filters.jobTitle);
    }

    // Apply expired documents filter
    if (filters.hasExpiredDocuments) {
      const now = new Date();
      filtered = filtered.filter(emp => 
        (emp.idExpires && emp.idExpires < now) ||
        (emp.tdlrExpires && emp.tdlrExpires < now)
      );
    }

    // Apply upcoming expirations filter
    if (filters.hasUpcomingExpirations) {
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(emp => 
        (emp.idExpires && emp.idExpires > now && emp.idExpires <= thirtyDaysFromNow) ||
        (emp.tdlrExpires && emp.tdlrExpires > now && emp.tdlrExpires <= thirtyDaysFromNow)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortOptions.field];
      let bValue: any = b[sortOptions.field];

      if (aValue instanceof Date) aValue = aValue.getTime();
      if (bValue instanceof Date) bValue = bValue.getTime();

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortOptions.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOptions.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredEmployees(filtered);
    setCurrentPage(1);
  };

  const calculateStats = () => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    const newStats: EmployeeStats = {
      total: employees.length,
      active: employees.filter(emp => emp.status === 'active').length,
      inactive: employees.filter(emp => emp.status === 'inactive').length,
      dismissed: employees.filter(emp => emp.status === 'dismissed').length,
      expiredDocuments: employees.filter(emp => 
        (emp.idExpires && emp.idExpires < now) ||
        (emp.tdlrExpires && emp.tdlrExpires < now)
      ).length,
      upcomingExpirations: employees.filter(emp => 
        (emp.idExpires && emp.idExpires > now && emp.idExpires <= thirtyDaysFromNow) ||
        (emp.tdlrExpires && emp.tdlrExpires > now && emp.tdlrExpires <= thirtyDaysFromNow)
      ).length
    };

    setStats(newStats);
  };

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    try {
      // Validate required fields
      if (!formData.name || !formData.jobTitle || !formData.hireDate || 
          !formData.hourlyWage || !formData.insurance || 
          !formData.vacation) {
        setError('Please fill in all required fields');
        return;
      }

      // Parse numeric values with validation
      const hourlyWage = parseFloat(formData.hourlyWage);
      const insurance = parseFloat(formData.insurance);
      const vacation = parseFloat(formData.vacation);
      const carFuel = formData.carFuel ? parseFloat(formData.carFuel) : 0;
      const markup = formData.markup ? parseFloat(formData.markup) : 0;
      const vacationHoursUsed = formData.vacationHoursUsed ? parseFloat(formData.vacationHoursUsed) : 0;
      const vacationYear = formData.vacationYear ? parseInt(formData.vacationYear) : new Date().getFullYear();

      // Validate parsed numbers
      if (isNaN(hourlyWage) || isNaN(insurance) || isNaN(vacation)) {
        setError('Please enter valid numbers for all rate fields');
        return;
      }

      // Parse skills ratings
      const skills = {
        handToolKnowledge: formData.handToolKnowledge ? parseInt(formData.handToolKnowledge) : undefined,
        installLightsOutlets: formData.installLightsOutlets ? parseInt(formData.installLightsOutlets) : undefined,
        installWire: formData.installWire ? parseInt(formData.installWire) : undefined,
        installBendConduit: formData.installBendConduit ? parseInt(formData.installBendConduit) : undefined,
        powerToolKnowledge: formData.powerToolKnowledge ? parseInt(formData.powerToolKnowledge) : undefined,
        pullWire: formData.pullWire ? parseInt(formData.pullWire) : undefined,
        readBlueprints: formData.readBlueprints ? parseInt(formData.readBlueprints) : undefined,
        repairWiringSystem: formData.repairWiringSystem ? parseInt(formData.repairWiringSystem) : undefined,
      };

      // Validate skills ratings
      if (!validateSkillsRatings(skills)) {
        setError('Skills ratings must be between 1 and 5');
        return;
      }

      // Calculate rates using the new calculation system
      const fica = hourlyWage * 0.0765; // Automatic FICA calculation (7.65% of hourly wage)
      const rateInputs = {
        hourlyWage,
        insurance,
        vacation,
        carFuel,
        markup,
      };
      
      // Calculate burdened rate (this is the "Rate" column in the Excel)
      const burdenedRate = calculateBurdenedRate(rateInputs);

      // Contractor rates are now calculated dynamically based on contractor configurations

      const employeeData = {
        name: formData.name.trim(),
        phone: formData.phone?.trim() || null,
        employeeNumber: formData.employeeNumber?.trim() || null,
        hireDate: new Date(formData.hireDate),
        jobTitle: formData.jobTitle.trim(),
        hourlyWage: hourlyWage,
        burdenedRate: burdenedRate,
        fica: fica,
        insurance: insurance,
        vacation: vacation,
        carFuel: carFuel || null,
        markup: markup,
        
        // Vacation tracking
        vacationHoursUsed: vacationHoursUsed,
        vacationYear: vacationYear,
        
        // Skills tracking
        skills: Object.keys(skills).some(key => skills[key as keyof typeof skills] !== undefined) ? skills : null,
        
        // Contractor rates are calculated dynamically
        
        // Documentation
        idType: formData.idType,
        idExpires: formData.idExpires ? new Date(formData.idExpires) : null,
        application: formData.application ?? false,
        tdlrNumber: formData.tdlrNumber?.trim() || null,
        tdlrExpires: formData.tdlrExpires ? new Date(formData.tdlrExpires) : null,
        cecEmployeeNumber: formData.cecEmployeeNumber?.trim() || null,
        scissorLiftTraining: formData.scissorLiftTraining ? new Date(formData.scissorLiftTraining) : null,
        firstAidCpr: formData.firstAidCpr ? new Date(formData.firstAidCpr) : null,
        status: formData.status,
        notes: formData.notes?.trim() || null,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUser.uid
      };

      console.log('Attempting to add employee:', employeeData);
      
      await addDoc(collection(db, 'employees'), employeeData);
      
      console.log('Employee added successfully');
      setError(null); // Clear any previous errors
      setShowAddModal(false);
      resetForm();
      fetchEmployees();
    } catch (err: any) {
      console.error('Detailed error adding employee:', err);
      setError(`Failed to add employee: ${err.message || 'Unknown error'}`);
    }
  };

  const handleEditEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee || !currentUser) return;

    try {
      const hourlyWage = parseFloat(formData.hourlyWage);
      const fica = hourlyWage * 0.08; // Automatic FICA calculation (8% of hourly wage)
      const insurance = parseFloat(formData.insurance);
      const vacation = parseFloat(formData.vacation);
      const carFuel = formData.carFuel ? parseFloat(formData.carFuel) : 0;
      const markup = parseFloat(formData.markup);
      
      // Calculate burdened rate (this is the "Rate" column in the Excel)
      const burdenedRate = hourlyWage + fica + insurance + vacation + carFuel + markup;

      // Contractor rates are now calculated dynamically based on contractor configurations

      const employeeData = {
        name: formData.name,
        hireDate: new Date(formData.hireDate),
        jobTitle: formData.jobTitle,
        hourlyWage: hourlyWage,
        burdenedRate: burdenedRate,
        fica: fica,
        insurance: insurance,
        vacation: vacation,
        carFuel: formData.carFuel ? parseFloat(formData.carFuel) : null,
        markup: markup,
        cecRate: cecRate,
        cecOtRate: cecOtRate,
        reliableRate: reliableRate,
        reliableOtRate: reliableOtRate,
        btcRate: btcRate,
        btcOtRate: btcOtRate,
        idType: formData.idType,
        idExpires: formData.idExpires ? new Date(formData.idExpires) : null,
        application: formData.application ?? false,
        tdlrNumber: formData.tdlrNumber || null,
        tdlrExpires: formData.tdlrExpires ? new Date(formData.tdlrExpires) : null,
        cecEmployeeNumber: formData.cecEmployeeNumber || null,
        scissorLiftTraining: formData.scissorLiftTraining ? new Date(formData.scissorLiftTraining) : null,
        firstAidCpr: formData.firstAidCpr ? new Date(formData.firstAidCpr) : null,
        status: formData.status,
        notes: formData.notes || null,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'employees', selectedEmployee.id), employeeData);
      setShowEditModal(false);
      setSelectedEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (err) {
      setError('Failed to update employee');
      console.error('Error updating employee:', err);
    }
  };

  const handleDeleteEmployee = async (employee: Employee) => {
    if (!window.confirm(`Are you sure you want to delete ${employee.name}?`)) return;

    try {
      await deleteDoc(doc(db, 'employees', employee.id));
      fetchEmployees();
    } catch (err) {
      setError('Failed to delete employee');
      console.error('Error deleting employee:', err);
    }
  };


  // Get active contractors for dynamic rate columns
  const getActiveContractors = () => {
    return contractors.filter(c => c.status === 'active').slice(0, 4); // Limit to 4 contractors for display
  };

  // Calculate contractor rates for an employee
  const getEmployeeContractorRates = (employee: Employee) => {
    const contractorMap = contractors.reduce((acc, contractor) => {
      acc[contractor.id] = contractor;
      return acc;
    }, {} as { [key: string]: Contractor });
    
    return calculateContractorRates(employee, contractorMap);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      employeeNumber: '',
      hireDate: '',
      jobTitle: '',
      hourlyWage: '',
      fica: '',
      insurance: '',
      vacation: '',
      carFuel: '',
      markup: '',
      vacationHoursUsed: '',
      vacationYear: new Date().getFullYear().toString(),
      // Skills (1-5 rating)
      handToolKnowledge: '',
      installLightsOutlets: '',
      installWire: '',
      installBendConduit: '',
      powerToolKnowledge: '',
      pullWire: '',
      readBlueprints: '',
      repairWiringSystem: '',
      cecRate: '',
      cecOtRate: '',
      reliableRate: '',
      reliableOtRate: '',
      btcRate: '',
      btcOtRate: '',
      idType: 'DL',
      idExpires: '',
      application: false,
      tdlrNumber: '',
      tdlrExpires: '',
      cecEmployeeNumber: '',
      scissorLiftTraining: '',
      firstAidCpr: '',
      status: 'active',
      notes: ''
    });
  };

  const toggleEmployeeExpansion = (employee: Employee) => {
    if (expandedEmployeeId === employee.id) {
      setExpandedEmployeeId(null);
      setEditingEmployeeId(null);
    } else {
      setExpandedEmployeeId(employee.id);
      setEditingEmployeeId(null);
      setEditData({
        name: employee.name,
        jobTitle: employee.jobTitle,
        hireDate: employee.hireDate.toISOString().split('T')[0],
        hourlyWage: employee.hourlyWage.toString(),
        insurance: employee.insurance.toString(),
        vacation: employee.vacation.toString(),
        carFuel: employee.carFuel?.toString() || '',
        tdlrNumber: employee.tdlrNumber || '',
        tdlrExpires: employee.tdlrExpires?.toISOString().split('T')[0] || '',
        cecEmployeeNumber: employee.cecEmployeeNumber || '',
        // Older records predating this field don't have it in Firestore at
        // all, so employee.application can be undefined - normalize to a
        // real boolean here so it's never undefined when saved back.
        application: employee.application ?? false,
        status: employee.status,
        notes: employee.notes || ''
      });
    }
  };

  const handleCardSave = async (employee: Employee) => {
    try {
      const hourlyWage = parseFloat(editData.hourlyWage);
      const fica = hourlyWage * 0.08;
      const insurance = parseFloat(editData.insurance);
      const vacation = parseFloat(editData.vacation);
      const carFuel = editData.carFuel ? parseFloat(editData.carFuel) : 0;

      // employee.markup can be missing on older records - without the
      // fallback, undefined + number = NaN, which then gets saved straight
      // into Firestore as the burdenedRate.
      const burdenedRate = hourlyWage + fica + insurance + vacation + carFuel + (employee.markup || 0);

      const updatedData = {
        name: editData.name.trim(),
        jobTitle: editData.jobTitle.trim(),
        hireDate: new Date(editData.hireDate),
        hourlyWage: hourlyWage,
        fica: fica,
        insurance: insurance,
        vacation: vacation,
        carFuel: carFuel || null,
        burdenedRate: burdenedRate,
        tdlrNumber: editData.tdlrNumber?.trim() || null,
        tdlrExpires: editData.tdlrExpires ? new Date(editData.tdlrExpires) : null,
        cecEmployeeNumber: editData.cecEmployeeNumber?.trim() || null,
        application: editData.application ?? false,
        status: editData.status,
        notes: editData.notes?.trim() || null,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'employees', employee.id), updatedData);
      setEditingEmployeeId(null);
      fetchEmployees();
    } catch (err) {
      setError('Failed to update employee');
      console.error('Error updating employee:', err);
    }
  };

  const openEditModal = (employee: Employee) => {
    setSelectedEmployee(employee);
    setFormData({
      name: employee.name,
      hireDate: employee.hireDate.toISOString().split('T')[0],
      jobTitle: employee.jobTitle,
      hourlyWage: employee.hourlyWage.toString(),
      fica: employee.fica.toString(),
      insurance: employee.insurance.toString(),
      vacation: employee.vacation.toString(),
      carFuel: employee.carFuel?.toString() || '',
      markup: (employee.markup || 0).toString(),
      idType: employee.idType,
      idExpires: employee.idExpires?.toISOString().split('T')[0] || '',
      // Older records predating this field don't have it in Firestore at
      // all, so employee.application can be undefined - normalize to a
      // real boolean here so it's never undefined when saved back.
      application: employee.application ?? false,
      tdlrNumber: employee.tdlrNumber || '',
      tdlrExpires: employee.tdlrExpires?.toISOString().split('T')[0] || '',
      cecEmployeeNumber: employee.cecEmployeeNumber || '',
      scissorLiftTraining: employee.scissorLiftTraining?.toISOString().split('T')[0] || '',
      firstAidCpr: employee.firstAidCpr?.toISOString().split('T')[0] || '',
      status: employee.status,
      notes: employee.notes || ''
    });
    setShowEditModal(true);
  };

  const openHistoryModal = async (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setSortField('weekEnding');
    setSortDirection('desc');
    
    try {
      await fetchEmployeeTimesheets(employee);
    } catch (err) {
      console.error('Error loading employee timesheets:', err);
      setError('Failed to load employee timesheet history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSort = (field: 'weekEnding' | 'contractor' | 'jobName' | 'hours') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedTimesheets = () => {
    return [...employeeTimesheets].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'weekEnding':
          aValue = a.weekEnding;
          bValue = b.weekEnding;
          break;
        case 'contractor':
          aValue = (a.contractorName || '').toLowerCase();
          bValue = (b.contractorName || '').toLowerCase();
          break;
        case 'jobName':
          aValue = (a.jobName || '').toLowerCase();
          bValue = (b.jobName || '').toLowerCase();
          break;
        case 'hours':
          aValue = a.employeeHours;
          bValue = b.employeeHours;
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleTimesheetClick = (timesheetId: string) => {
    navigate(`/timesheets/entry/${timesheetId}`);
  };

  const fetchEmployeeTimesheets = async (employee: Employee) => {
    try {
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      const q = query(
        timesheetsRef, 
        orderBy('weekEnding', 'desc')
      );
      const snapshot = await getDocs(q);
      
      // Filter timesheets for this employee and calculate total hours
      const employeeTimesheets = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            weekEnding: data.weekEnding?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date()
          };
        })
        .filter(timesheet => {
          // Check if this employee has entries in this timesheet
          return timesheet.entries && timesheet.entries.some((entry: any) => 
            entry.employeeName?.toLowerCase() === employee.name.toLowerCase()
          );
        })
        .map(timesheet => {
          // Calculate total hours for this employee in this timesheet
          const employeeEntries = timesheet.entries.filter((entry: any) => 
            entry.employeeName?.toLowerCase() === employee.name.toLowerCase()
          );
          
          const totalHours = employeeEntries.reduce((total: number, entry: any) => {
            return total + Object.values(entry.hours || {}).reduce((sum: number, hours: any) => sum + (Number(hours) || 0), 0);
          }, 0);
          
          return {
            ...timesheet,
            employeeHours: totalHours,
            employeeEntries: employeeEntries.length
          };
        })
        .filter(timesheet => timesheet.employeeHours > 0); // Only show timesheets where employee worked
      
      setEmployeeTimesheets(employeeTimesheets);
    } catch (err) {
      console.error('Error fetching employee timesheets:', err);
      throw err;
    }
  };

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString();
  };

  const isExpired = (date: Date | null | undefined) => {
    if (!date) return false;
    return date < new Date();
  };

  const isExpiringSoon = (date: Date | null | undefined) => {
    if (!date) return false;
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return date > now && date <= thirtyDaysFromNow;
  };

  // Get unique job titles for filter. Filter out falsy values first -
  // employees missing a jobTitle otherwise land an undefined entry in the
  // Set, which renders as an <option key={undefined}>, triggering React's
  // "missing key" warning.
  const uniqueJobTitles = [...new Set(employees.map(emp => emp.jobTitle).filter(Boolean))].sort();

  // Pagination
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Employee Management</h1>
        <div className="flex space-x-4">
          <button
            onClick={() => navigate('/employees/performance')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <span>📊</span>
            <span>Performance Analytics</span>
          </button>
          <button
            onClick={() => setShowDataImportModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Import/Update Employee Data
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Employee
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('employees')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'employees'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Employee Management
            </button>
            <button
              onClick={() => setActiveTab('tdlr')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tdlr'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              TDLR License Management
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'tdlr' ? (
        <TDLRLicenseManagement 
          employees={employees} 
          onRefreshEmployees={fetchEmployees}
          onUpdateEmployee={updateEmployeeLocal}
        />
      ) : (
        <>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-yellow-600">{stats.inactive}</div>
          <div className="text-sm text-gray-600">Inactive</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">{stats.dismissed}</div>
          <div className="text-sm text-gray-600">Dismissed</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">{stats.expiredDocuments}</div>
          <div className="text-sm text-gray-600">Expired Docs</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-orange-600">{stats.upcomingExpirations}</div>
          <div className="text-sm text-gray-600">Expiring Soon</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Name, job title, TDLR..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
            <select
              value={filters.jobTitle}
              onChange={(e) => setFilters({ ...filters, jobTitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {uniqueJobTitles.map(title => (
                <option key={title} value={title}>{title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={`${sortOptions.field}-${sortOptions.direction}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-');
                setSortOptions({ field: field as any, direction: direction as any });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="hireDate-asc">Hire Date (Oldest)</option>
              <option value="hireDate-desc">Hire Date (Newest)</option>
              <option value="hourlyWage-desc">Wage (Highest)</option>
              <option value="hourlyWage-asc">Wage (Lowest)</option>
            </select>
          </div>
        </div>
        <div className="flex space-x-4 mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.hasExpiredDocuments}
              onChange={(e) => setFilters({ ...filters, hasExpiredDocuments: e.target.checked })}
              className="mr-2"
            />
            Expired Documents
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.hasUpcomingExpirations}
              onChange={(e) => setFilters({ ...filters, hasUpcomingExpirations: e.target.checked })}
              className="mr-2"
            />
            Expiring Soon (30 days)
          </label>
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Hourly Wage</th>
                {getActiveContractors().map(contractor => {
                  // Use prefix if available, or "CEC" for CEC Facilities Group, otherwise company name
                  const displayName = contractor.invoicePrefix || 
                    (contractor.companyName === 'CEC Facilities Group' ? 'CEC' : contractor.companyName);
                  return (
                    <th key={contractor.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {displayName} Rate
                    </th>
                  );
                })}
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">TDLR Expires</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentEmployees.map((employee) => (
                <React.Fragment key={employee.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleEmployeeExpansion(employee)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                    {employee.tdlrNumber && (
                      <div className="text-sm text-gray-500">TDLR: {employee.tdlrNumber}</div>
                    )}
                    {/* Show status indicator as small badge */}
                    <span className={`inline-flex mt-1 px-2 py-1 text-xs font-semibold rounded-full ${
                      employee.status === 'active' ? 'bg-green-100 text-green-800' :
                      employee.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {employee.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center font-medium">
                    ${employee.hourlyWage?.toFixed(2) || '0.00'}
                  </td>
                  {(() => {
                    const activeContractors = getActiveContractors();
                    
                    return activeContractors.map(contractor => {
                      // Calculate rate based on contractor's specific config
                      const baseInputs = {
                        hourlyWage: employee.hourlyWage || 0,
                        insurance: employee.insurance || 0,
                        vacation: employee.vacation || 0,
                        carFuel: employee.carFuel || 0,
                        markup: employee.markup || 0
                      };
                      
                      const contractorConfig = {
                        markupType: contractor.billing.markupType,
                        markupValue: contractor.billing.markupValue
                      };
                      
                      const rate = calculateBurdenedRate(baseInputs, contractorConfig);
                      
                      return (
                        <td key={contractor.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center font-medium">
                          ${rate.toFixed(2)}
                        </td>
                      );
                    });
                  })()}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <span className={
                      isExpired(employee.tdlrExpires) ? 'text-red-600 font-semibold' :
                      isExpiringSoon(employee.tdlrExpires) ? 'text-orange-600 font-semibold' :
                      'text-gray-900'
                    }>
                      {formatDate(employee.tdlrExpires)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openHistoryModal(employee)}
                        className="text-green-600 hover:text-green-900 font-medium"
                        title="View timesheet history"
                      >
                        History
                      </button>
                      <button
                        onClick={() => handleDeleteEmployee(employee)}
                        className="text-red-600 hover:text-red-900 font-medium"
                        title="Delete employee"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                
                {/* Expandable Employee Details Card */}
                {expandedEmployeeId === employee.id && (
                  <tr>
                    <td colSpan={8} className="px-0 py-0">
                      <div className="bg-gray-50 border-t border-gray-200">
                        <div className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{employee.name}</h3>
                              <p className="text-gray-600">{employee.jobTitle}</p>
                            </div>
                            <div className="flex space-x-2">
                              {editingEmployeeId !== employee.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingEmployeeId(employee.id);
                                  }}
                                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedEmployeeId(null);
                                  setEditingEmployeeId(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 text-xl"
                              >
                                ×
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Basic Information */}
                            <div className="space-y-3">
                              <h4 className="text-md font-semibold text-gray-800 border-b pb-1">Basic Information</h4>
                              
                              {editingEmployeeId === employee.id ? (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                      type="text"
                                      value={editData.name}
                                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                                    <input
                                      type="text"
                                      value={editData.jobTitle}
                                      onChange={(e) => setEditData({ ...editData, jobTitle: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                                    <input
                                      type="date"
                                      value={editData.hireDate}
                                      onChange={(e) => setEditData({ ...editData, hireDate: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                      value={editData.status}
                                      onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="active">Active</option>
                                      <option value="inactive">Inactive</option>
                                      <option value="dismissed">Dismissed</option>
                                    </select>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {employee.phone && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">Phone:</span>
                                      <span className="ml-2 text-gray-900">{employee.phone}</span>
                                    </div>
                                  )}
                                  {employee.employeeNumber && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">Employee #:</span>
                                      <span className="ml-2 text-gray-900">{employee.employeeNumber}</span>
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Hire Date:</span>
                                    <span className="ml-2 text-gray-900">{employee.hireDate.toLocaleDateString()}</span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Status:</span>
                                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                                      employee.status === 'active' ? 'bg-green-100 text-green-800' :
                                      employee.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {employee.status}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Rate Information */}
                            <div className="space-y-3">
                              <h4 className="text-md font-semibold text-gray-800 border-b pb-1">Rate Information</h4>
                              
                              {editingEmployeeId === employee.id ? (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Wage</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editData.hourlyWage}
                                      onChange={(e) => setEditData({ ...editData, hourlyWage: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editData.insurance}
                                      onChange={(e) => setEditData({ ...editData, insurance: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Vacation</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editData.vacation}
                                      onChange={(e) => setEditData({ ...editData, vacation: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Car/Fuel</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editData.carFuel}
                                      onChange={(e) => setEditData({ ...editData, carFuel: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Hourly Wage:</span>
                                    <span className="ml-2 text-gray-900">${employee.hourlyWage?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-700" title="Burdened Rate = Hourly Wage + FICA + Insurance + Vacation + Car/Fuel + Markup">Burdened Rate:</span>
                                    <span className="ml-2 text-gray-900">${employee.burdenedRate?.toFixed(2) || '0.00'}</span>
                                    <span className="ml-1 text-xs text-gray-400 cursor-help" title="Calculation: $${employee.hourlyWage || 0} (wage) + $${((employee.hourlyWage || 0) * 0.08).toFixed(2)} (FICA) + $${employee.insurance || 0} (insurance) + $${employee.vacation || 0} (vacation) + $${employee.carFuel || 0} (car/fuel) + $${employee.markup || 0} (markup)">ⓘ</span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Insurance:</span>
                                    <span className="ml-2 text-gray-900">${employee.insurance?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Vacation:</span>
                                    <span className="ml-2 text-gray-900">${employee.vacation?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  {employee.carFuel && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">Car/Fuel:</span>
                                      <span className="ml-2 text-gray-900">${employee.carFuel.toFixed(2)}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            {/* TDLR & Documentation */}
                            <div className="space-y-3">
                              <h4 className="text-md font-semibold text-gray-800 border-b pb-1">TDLR & Documentation</h4>
                              
                              {editingEmployeeId === employee.id ? (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">TDLR Number</label>
                                    <input
                                      type="text"
                                      value={editData.tdlrNumber}
                                      onChange={(e) => setEditData({ ...editData, tdlrNumber: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">TDLR Expires</label>
                                    <input
                                      type="date"
                                      value={editData.tdlrExpires}
                                      onChange={(e) => setEditData({ ...editData, tdlrExpires: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">CEC Employee Number</label>
                                    <input
                                      type="text"
                                      value={editData.cecEmployeeNumber}
                                      onChange={(e) => setEditData({ ...editData, cecEmployeeNumber: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id="card-application"
                                      checked={editData.application}
                                      onChange={(e) => setEditData({ ...editData, application: e.target.checked })}
                                      className="mr-2"
                                    />
                                    <label htmlFor="card-application" className="text-sm font-medium text-gray-700">Application on File</label>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {employee.tdlrNumber && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">TDLR:</span>
                                      <span className="ml-2 text-gray-900">{employee.tdlrNumber}</span>
                                      {employee.tdlrExpires && (
                                        <span className={`ml-2 text-sm ${
                                          isExpired(employee.tdlrExpires) ? 'text-red-600 font-semibold' :
                                          isExpiringSoon(employee.tdlrExpires) ? 'text-orange-600 font-semibold' :
                                          'text-gray-600'
                                        }`}>
                                          (Expires: {formatDate(employee.tdlrExpires)})
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {employee.cecEmployeeNumber && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">CEC Employee #:</span>
                                      <span className="ml-2 text-gray-900">{employee.cecEmployeeNumber}</span>
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Application on File:</span>
                                    <span className="ml-2 text-gray-900">{employee.application ? 'Yes' : 'No'}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Skills Section */}
                          {employee.skills && Object.keys(employee.skills).some(key => employee.skills![key as keyof typeof employee.skills] !== undefined) && (
                            <div className="mt-4">
                              <h4 className="text-md font-semibold text-gray-800 border-b pb-1 mb-3">Skills Assessment</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {employee.skills.handToolKnowledge && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Hand Tools:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.handToolKnowledge)}</span>
                                  </div>
                                )}
                                {employee.skills.installLightsOutlets && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Lights/Outlets:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.installLightsOutlets)}</span>
                                  </div>
                                )}
                                {employee.skills.installWire && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Install Wire:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.installWire)}</span>
                                  </div>
                                )}
                                {employee.skills.installBendConduit && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Conduit:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.installBendConduit)}</span>
                                  </div>
                                )}
                                {employee.skills.powerToolKnowledge && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Power Tools:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.powerToolKnowledge)}</span>
                                  </div>
                                )}
                                {employee.skills.pullWire && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Pull Wire:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.pullWire)}</span>
                                  </div>
                                )}
                                {employee.skills.readBlueprints && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Blueprints:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.readBlueprints)}</span>
                                  </div>
                                )}
                                {employee.skills.repairWiringSystem && (
                                  <div className="text-sm">
                                    <span className="font-medium text-gray-700">Wiring Repair:</span>
                                    <span className="ml-2 text-gray-900">{getSkillRatingText(employee.skills.repairWiringSystem)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Vacation Tracking */}
                          {(employee.vacationHoursUsed !== undefined || employee.vacationYear !== undefined) && (
                            <div className="mt-4">
                              <h4 className="text-md font-semibold text-gray-800 border-b pb-1 mb-3">Vacation Tracking</h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="text-sm">
                                  <span className="font-medium text-gray-700">Hours Used:</span>
                                  <span className="ml-2 text-gray-900">{employee.vacationHoursUsed || 0} hrs</span>
                                </div>
                                <div className="text-sm">
                                  <span className="font-medium text-gray-700">Year:</span>
                                  <span className="ml-2 text-gray-900">{employee.vacationYear || new Date().getFullYear()}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Contractor-Specific Data */}
                          {employee.contractorSpecificData && contractors.length > 0 && Object.keys(employee.contractorSpecificData).length > 0 && (
                            <div className="mt-4">
                              <h4 className="text-md font-semibold text-gray-800 border-b pb-1 mb-3">Contractor-Specific Information</h4>
                              {Object.entries(employee.contractorSpecificData).map(([contractorId, data]) => {
                                const contractor = contractors.find(c => c.id === contractorId);
                                if (!contractor || !contractor.customEmployeeFields) return null;
                                
                                return (
                                  <div key={contractorId} className="mb-4 p-3 bg-gray-50 rounded-md">
                                    <h5 className="font-medium text-gray-800 mb-2">{contractor.companyName}</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {Object.entries(contractor.customEmployeeFields)
                                        .filter(([fieldKey]) => data[fieldKey] !== undefined && data[fieldKey] !== null && data[fieldKey] !== '')
                                        .map(([fieldKey, fieldConfig]) => (
                                          <div key={fieldKey} className="text-sm">
                                            <span className="font-medium text-gray-700">{fieldConfig.label}:</span>
                                            <span className="ml-2 text-gray-900">
                                              {fieldConfig.type === 'boolean' 
                                                ? (data[fieldKey] ? 'Yes' : 'No')
                                                : fieldConfig.type === 'date' 
                                                  ? new Date(data[fieldKey]).toLocaleDateString()
                                                  : String(data[fieldKey])
                                              }
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Notes Section */}
                          <div className="mt-4">
                            <h4 className="text-md font-semibold text-gray-800 border-b pb-1 mb-3">Notes</h4>
                            {editingEmployeeId === employee.id ? (
                              <textarea
                                rows={2}
                                value={editData.notes}
                                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Add any notes about this employee..."
                              />
                            ) : (
                              <p className="text-gray-700 text-sm">
                                {employee.notes || 'No notes'}
                              </p>
                            )}
                          </div>

                          {/* Action Buttons */}
                          {editingEmployeeId === employee.id && (
                            <div className="flex justify-end space-x-3 mt-4 pt-3 border-t">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingEmployeeId(null);
                                }}
                                className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCardSave(employee);
                                }}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                              >
                                Save Changes
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredEmployees.length)}</span> of{' '}
                  <span className="font-medium">{filteredEmployees.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Employee</h3>
              <form onSubmit={handleAddEmployee} className="space-y-6">
                
                {/* Basic Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Employee Number</label>
                      <input
                        type="text"
                        value={formData.employeeNumber}
                        onChange={(e) => setFormData({ ...formData, employeeNumber: e.target.value })}
                        placeholder="EMP001"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Job Title *</label>
                      <input
                        type="text"
                        required
                        value={formData.jobTitle}
                        onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Hire Date *</label>
                      <input
                        type="date"
                        required
                        value={formData.hireDate}
                        onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="dismissed">Dismissed</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Vacation Tracking */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Vacation Hours Used</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        value={formData.vacationHoursUsed}
                        onChange={(e) => setFormData({ ...formData, vacationHoursUsed: e.target.value })}
                        placeholder="0"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Vacation Year</label>
                      <input
                        type="number"
                        min="2020"
                        max="2030"
                        value={formData.vacationYear}
                        onChange={(e) => setFormData({ ...formData, vacationYear: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Rate Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Rate Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Hourly Wage *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={formData.hourlyWage}
                        onChange={(e) => setFormData({ ...formData, hourlyWage: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">FICA (Auto-calculated)</label>
                      <input
                        type="text"
                        disabled
                        value={formData.hourlyWage ? `$${(parseFloat(formData.hourlyWage || '0') * 0.08).toFixed(2)}` : '$0.00'}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                      />
                      <p className="text-xs text-gray-500 mt-1">Automatically calculated as 8% of hourly wage</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Insurance *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={formData.insurance}
                        onChange={(e) => setFormData({ ...formData, insurance: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Vacation *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={formData.vacation}
                        onChange={(e) => setFormData({ ...formData, vacation: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Car/Fuel</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.carFuel}
                        onChange={(e) => setFormData({ ...formData, carFuel: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">TDLR Number</label>
                      <input
                        type="text"
                        value={formData.tdlrNumber}
                        onChange={(e) => setFormData({ ...formData, tdlrNumber: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">TDLR Expires</label>
                      <input
                        type="date"
                        value={formData.tdlrExpires}
                        onChange={(e) => setFormData({ ...formData, tdlrExpires: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">CEC Employee Number</label>
                      <input
                        type="text"
                        value={formData.cecEmployeeNumber}
                        onChange={(e) => setFormData({ ...formData, cecEmployeeNumber: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="edit-application"
                        checked={formData.application}
                        onChange={(e) => setFormData({ ...formData, application: e.target.checked })}
                        className="mr-2"
                      />
                      <label htmlFor="edit-application" className="text-sm font-medium text-gray-700">Application on File</label>
                    </div>
                  </div>
                </div>

                {/* Skills Assessment */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Skills Assessment (1-5 Rating)</h4>
                  <p className="text-sm text-gray-600 mb-3">Rate each skill from 1 (Beginner) to 5 (Expert). Leave blank if not assessed.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Hand Tool Knowledge</label>
                      <select
                        value={formData.handToolKnowledge}
                        onChange={(e) => setFormData({ ...formData, handToolKnowledge: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Install Lights/Outlets</label>
                      <select
                        value={formData.installLightsOutlets}
                        onChange={(e) => setFormData({ ...formData, installLightsOutlets: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Install Wire</label>
                      <select
                        value={formData.installWire}
                        onChange={(e) => setFormData({ ...formData, installWire: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Install/Bend Conduit</label>
                      <select
                        value={formData.installBendConduit}
                        onChange={(e) => setFormData({ ...formData, installBendConduit: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Power Tool Knowledge</label>
                      <select
                        value={formData.powerToolKnowledge}
                        onChange={(e) => setFormData({ ...formData, powerToolKnowledge: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pull Wire</label>
                      <select
                        value={formData.pullWire}
                        onChange={(e) => setFormData({ ...formData, pullWire: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Read Blueprints</label>
                      <select
                        value={formData.readBlueprints}
                        onChange={(e) => setFormData({ ...formData, readBlueprints: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Repair Wiring System</label>
                      <select
                        value={formData.repairWiringSystem}
                        onChange={(e) => setFormData({ ...formData, repairWiringSystem: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not Rated</option>
                        <option value="1">1 - Beginner</option>
                        <option value="2">2 - Basic</option>
                        <option value="3">3 - Intermediate</option>
                        <option value="4">4 - Advanced</option>
                        <option value="5">5 - Expert</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Employee
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && selectedEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Employee: {selectedEmployee.name}</h3>
              <form onSubmit={handleEditEmployee} className="space-y-6">
                
                {/* Basic Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Job Title *</label>
                      <input
                        type="text"
                        required
                        value={formData.jobTitle}
                        onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Hire Date *</label>
                      <input
                        type="date"
                        required
                        value={formData.hireDate}
                        onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="dismissed">Dismissed</option>
                      </select>
                    </div>
                  </div>
                </div>


                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedEmployee(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Update Employee
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Employee Timesheet History Modal */}
      {showHistoryModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    Timesheet History
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">
                    {selectedEmployee.name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setSelectedEmployee(null);
                    setEmployeeTimesheets([]);
                  }}
                  className="text-blue-200 hover:text-white transition-colors duration-200 text-2xl font-light"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">

              {historyLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600 text-lg">Loading timesheet history...</span>
                </div>
              ) : employeeTimesheets.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-gray-400 text-6xl mb-4">📋</div>
                  <p className="text-gray-500 text-lg">No timesheet history found for this employee.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">
                      💡 <span className="font-medium">Tip:</span> Click on any timesheet row to view or edit details
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th 
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-150"
                            onClick={() => handleSort('weekEnding')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Week Ending</span>
                              <span className="text-gray-400">
                                {sortField === 'weekEnding' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th 
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-150"
                            onClick={() => handleSort('contractor')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Contractor</span>
                              <span className="text-gray-400">
                                {sortField === 'contractor' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th 
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-150"
                            onClick={() => handleSort('jobName')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Job Name</span>
                              <span className="text-gray-400">
                                {sortField === 'jobName' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Job Number
                          </th>
                          <th 
                            className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors duration-150"
                            onClick={() => handleSort('hours')}
                          >
                            <div className="flex items-center justify-end space-x-1">
                              <span>Total Hours</span>
                              <span className="text-gray-400">
                                {sortField === 'hours' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Entries
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {getSortedTimesheets().map((timesheet, index) => (
                          <tr 
                            key={timesheet.id} 
                            onClick={() => handleTimesheetClick(timesheet.id)}
                            className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-all duration-150 hover:shadow-sm hover:scale-[1.01] group`}
                            title="Click to view/edit this timesheet"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {timesheet.weekEnding.toLocaleDateString('en-US', { 
                                  weekday: 'short',
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {timesheet.contractorName ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {timesheet.contractorName}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 font-medium">
                                {timesheet.jobName || <span className="text-gray-400">N/A</span>}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {timesheet.jobNumber || <span className="text-gray-400">N/A</span>}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                                {timesheet.employeeHours.toFixed(1)}h
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <div className="text-sm text-gray-600 font-medium">
                                  {timesheet.employeeEntries}
                                </div>
                                <svg 
                                  className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors duration-150" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-4 text-lg">📊 Summary Statistics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-700">{employeeTimesheets.length}</div>
                        <div className="text-sm text-blue-600 font-medium">Total Timesheets</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-700">
                          {employeeTimesheets.reduce((total, ts) => total + ts.employeeHours, 0).toFixed(1)}h
                        </div>
                        <div className="text-sm text-green-600 font-medium">Total Hours</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-700">
                          {employeeTimesheets.reduce((total, ts) => total + ts.employeeEntries, 0)}
                        </div>
                        <div className="text-sm text-purple-600 font-medium">Total Entries</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-700">
                          {(() => {
                            // Group timesheets by week ending date and sum hours for each week
                            const weeklyHours = employeeTimesheets.reduce((acc, ts) => {
                              const weekKey = ts.weekEnding.toDateString();
                              acc[weekKey] = (acc[weekKey] || 0) + ts.employeeHours;
                              return acc;
                            }, {} as Record<string, number>);
                            
                            const totalWeeks = Object.keys(weeklyHours).length;
                            const totalHours = Object.values(weeklyHours).reduce((sum, hours) => sum + hours, 0);
                            
                            return totalWeeks > 0 ? (totalHours / totalWeeks).toFixed(1) : '0.0';
                          })()}h
                        </div>
                        <div className="text-sm text-orange-600 font-medium">Avg Hours/Week</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowHistoryModal(false);
                    setSelectedEmployee(null);
                    setEmployeeTimesheets([]);
                  }}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Employee Data Importer Modal */}
      {showDataImportModal && (
        <EmployeeDataImporter
          onClose={() => setShowDataImportModal(false)}
          onSuccess={() => {
            fetchEmployees();
            setShowDataImportModal(false);
          }}
        />
      )}

        </>
      )}
    </div>
  );
};


export default EmployeeManagement;
